import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Download } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const PILLARS = ["asset", "digital", "experience", "professions"];
const pct = (v) => Math.round((v ?? 0) * 1000) / 10;
const fmt = (n) => `€${(n ?? 0).toFixed(2)}`;

export default function AffiliazioneAdminPanel() {
  const [ov, setOv] = useState(null);
  const [form, setForm] = useState(null);
  const [range, setRange] = useState({ from: "", to: "" });
  const [busy, setBusy] = useState(false);

  const load = async (r = range) => {
    try {
      const { data } = await api.get("/admin/referral-overview", { params: { date_from: r.from || undefined, date_to: r.to || undefined } });
      setOv(data);
      if (!form) setForm({ platform_margin_pct: pct(data.settings.platform_margin_pct), referral_pct: pct(data.settings.referral_pct), max_ratio: pct(data.settings.max_ratio),
        margin_by_pillar: Object.fromEntries(PILLARS.map((p) => [p, data.settings.margin_by_pillar?.[p] != null ? pct(data.settings.margin_by_pillar[p]) : ""])) });
    } catch {}
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true);
    try {
      const body = { platform_margin_pct: Number(form.platform_margin_pct) / 100, referral_pct: Number(form.referral_pct) / 100, max_ratio: Number(form.max_ratio) / 100,
        margin_by_pillar: Object.fromEntries(Object.entries(form.margin_by_pillar).filter(([, v]) => v !== "").map(([k, v]) => [k, Number(v) / 100])) };
      await api.patch("/admin/referral-settings", body); toast.success("Impostazioni affiliazione salvate"); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const downloadCsv = async () => {
    const { data } = await api.get("/admin/referral-audit.csv", { responseType: "blob" });
    const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = "referral_audit.csv"; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="referral-admin-panel" className="border border-neutral-200 rounded-2xl p-4 space-y-4">
      <div><h2 className="font-display font-bold">Programma Affiliazione (1 livello)</h2><p className="text-xs text-neutral-500">Commissioni solo da ordini pagati; nessun bonus di registrazione; nessun livello superiore.</p></div>
      {ov?.alert && <div data-testid="referral-alert" className="flex items-center gap-2 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm p-3"><AlertTriangle size={16} />Attenzione: le commissioni pagate superano la soglia del {pct(ov.settings.max_ratio)}% del margine reale.</div>}
      <div className="flex gap-2 items-end">
        <div className="flex-1"><label className="text-[11px] text-neutral-500">Dal</label><Input data-testid="ref-range-from" type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="h-9" /></div>
        <div className="flex-1"><label className="text-[11px] text-neutral-500">Al</label><Input data-testid="ref-range-to" type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="h-9" /></div>
        <Button data-testid="ref-range-apply" onClick={() => load(range)} variant="outline" className="h-9 rounded-full">Applica</Button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-xl bg-neutral-50 p-3"><p className="text-xs text-neutral-500">Margine reale</p><p data-testid="ref-margin-total" className="font-display font-extrabold">{fmt(ov?.margin_total)}</p></div>
        <div className="rounded-xl bg-neutral-50 p-3"><p className="text-xs text-neutral-500">Commissioni</p><p data-testid="ref-comm-total" className="font-display font-extrabold">{fmt(ov?.commissions_total)}</p></div>
        <div className={`rounded-xl p-3 ${ov?.alert ? "bg-red-50" : "bg-neutral-50"}`}><p className="text-xs text-neutral-500">Rapporto</p><p data-testid="ref-ratio" className="font-display font-extrabold">{pct(ov?.ratio)}%</p></div>
      </div>
      {form && (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            {[["platform_margin_pct", "Margine piattaforma %"], ["referral_pct", "Commissione affiliazione % del margine"], ["max_ratio", "Soglia max % del margine"]].map(([k, l]) => (
              <div key={k}><label className="text-[11px] text-neutral-500">{l}</label><Input data-testid={`ref-set-${k}`} type="number" step="0.1" min="0" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-9" /></div>
            ))}
          </div>
          <div className="grid grid-cols-4 gap-2">
            {PILLARS.map((p) => <div key={p}><label className="text-[11px] text-neutral-500 capitalize">Margine {p} %</label><Input data-testid={`ref-set-pillar-${p}`} type="number" step="0.1" min="0" placeholder="default" value={form.margin_by_pillar[p]} onChange={(e) => setForm({ ...form, margin_by_pillar: { ...form.margin_by_pillar, [p]: e.target.value } })} className="h-9" /></div>)}
          </div>
          <div className="flex gap-2">
            <Button data-testid="ref-settings-save" onClick={save} disabled={busy} className="flex-1 h-10 rounded-full bg-black text-white">{busy ? "…" : "Salva impostazioni"}</Button>
            <Button data-testid="ref-audit-csv" onClick={downloadCsv} variant="outline" className="h-10 rounded-full"><Download size={15} className="mr-1" />Log CSV</Button>
          </div>
        </div>
      )}
    </div>
  );
}
